import { LOCALES } from 'constants/app-config'
import { ensureRouteModulesLoaded } from 'store/domains/utils/routeModules'
import {
  PRELOAD_DOMAINS_PRIVATE,
  PRELOAD_GUEST_ROUTES,
} from 'store/domains/utils/routeDomainIndex'
import { ensureI18nNamespacesLoaded, AUTHORIZED_SHELL_I18N_NAMESPACES } from 'i18nProduct'
import { preloadLazyComponent } from 'utils/preloadLazyComponent'
import { store } from 'store'

export const PRELOAD_POOL = {
  PRIVATE: 'private',
  GUEST: 'guest',
}

const PRELOAD_DOMAINS_BY_POOL = {
  [PRELOAD_POOL.PRIVATE]: PRELOAD_DOMAINS_PRIVATE,
  [PRELOAD_POOL.GUEST]: PRELOAD_GUEST_ROUTES,
}

const localePrefix = () => {
  const fromStore = store.getState()?.app?.i18n
  if (fromStore) {
    return `/${fromStore}`
  }

  const seg = global.location.pathname.split('/').filter(Boolean)[0]
  if (LOCALES.includes(seg)) {
    return `/${seg}`
  }

  return '/ru'
}

const toLocalizedPathname = (routePath) => {
  const base = localePrefix()
  if (!routePath) {
    return base
  }

  const suffix = routePath.startsWith('/') ? routePath : `/${routePath}`
  return `${base}${suffix}`
}

const preloadStartedPools = new Set()

const scheduleIdleTask = (fn) => {
  if (typeof requestIdleCallback === 'function') {
    return requestIdleCallback(fn, { timeout: 5000 })
  }

  return setTimeout(fn, 200)
}

const cancelIdleTask = (id) => {
  if (typeof cancelIdleCallback === 'function') {
    return cancelIdleCallback(id)
  }

  return clearTimeout(id)
}

const preloadDomain = ({
  pathname,
  routeKey,
  extraI18nNamespaces = [],
  component = null,
  loadI18n = true,
}) => {
  const localizedPathname = pathname || toLocalizedPathname('')

  const tasks = [ensureRouteModulesLoaded(localizedPathname, routeKey || null)]

  if (loadI18n) {
    tasks.push(
      ensureI18nNamespacesLoaded({
        pathname: localizedPathname,
        routeKey: routeKey || '',
        routePath: localizedPathname,
        extraNamespaces: extraI18nNamespaces,
      }),
    )
  }

  if (component) {
    tasks.push(preloadLazyComponent(component))
  }

  return Promise.all(tasks).catch(() => {})
}

export const startBackgroundPreload = ({ skipRouteKey, pool = PRELOAD_POOL.PRIVATE } = {}) => {
  if (preloadStartedPools.has(pool)) {
    return () => {}
  }

  preloadStartedPools.add(pool)

  const preloadDomains = PRELOAD_DOMAINS_BY_POOL[pool] ?? PRELOAD_DOMAINS_PRIVATE
  const extraI18nNamespaces =
    pool === PRELOAD_POOL.PRIVATE ? AUTHORIZED_SHELL_I18N_NAMESPACES : []

  const remaining = preloadDomains.filter((entry) => entry.routeKey !== skipRouteKey)
  let idx = 0
  let taskId = null

  const loadNext = () => {
    if (idx >= remaining.length) {
      return
    }

    taskId = scheduleIdleTask(async () => {
      const entry = remaining[idx]
      await preloadDomain({
        pathname: toLocalizedPathname(entry.routePath),
        routeKey: entry.routeKey,
        extraI18nNamespaces,
        component: entry.component ?? null,
        loadI18n: false,
      })
      idx++
      loadNext()
    })
  }

  loadNext()

  return () => {
    if (taskId != null) {
      cancelIdleTask(taskId)
    }

    preloadStartedPools.delete(pool)
  }
}

export const preloadDomainForPathname = (pathname, routeKey) =>
  preloadDomain({ pathname, routeKey })
